/**
 * SEO.jsx — lightweight head manager (no external deps).
 * Updates title, meta description, keywords, Open Graph,
 * Twitter Card, canonical URL, robots, and JSON-LD on every
 * route change so Googlebot sees correct data per page.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL  = 'https://movementgreen.com.au';
const OG_IMAGE  = `${BASE_URL}/og-image.jpg`;
const SITE_NAME = 'Movement Green';

/* ── Helpers ─────────────────────────────────────────── */
function setMeta(attr, val, content) {
  let el = document.querySelector(`meta[${attr}="${val}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, val);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/* ── Component ───────────────────────────────────────── */
export default function SEO({
  title,
  description,
  keywords,
  schema,
  noindex = false,
}) {
  const { pathname } = useLocation();
  const canonical    = `${BASE_URL}${pathname === '/' ? '' : pathname}`;

  useEffect(() => {
    /* Title */
    document.title = title;

    /* Core meta */
    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);

    /* Robots */
    setMeta('name', 'robots', noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
    );

    /* Open Graph */
    setMeta('property', 'og:type',        'website');
    setMeta('property', 'og:site_name',   SITE_NAME);
    setMeta('property', 'og:title',       title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url',         canonical);
    setMeta('property', 'og:image',       OG_IMAGE);
    setMeta('property', 'og:locale',      'en_AU');

    /* Twitter Card */
    setMeta('name', 'twitter:card',        'summary_large_image');
    setMeta('name', 'twitter:title',       title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image',       OG_IMAGE);

    /* Canonical */
    setLink('canonical', canonical);

    /* JSON-LD structured data */
    const prev = document.getElementById('seo-ld-json');
    if (prev) prev.remove();
    if (schema) {
      const s = document.createElement('script');
      s.id   = 'seo-ld-json';
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    }
  }, [title, description, keywords, schema, noindex, canonical]);

  return null;
}

/* ── Shared schema fragments used across pages ───────── */
export const BUSINESS_SCHEMA = {
  '@type': 'LocalBusiness',
  '@id':   `${BASE_URL}/#business`,
  name:    SITE_NAME,
  description:
    'Movement Green is a VEU Accredited Person helping Victorian homeowners access government rebates on heat pumps, reverse-cycle air conditioning, LED lighting and more under the Victorian Energy Upgrades (VEU) program.',
  hasCredential: {
    '@type':              'EducationalOccupationalCredential',
    name:                 'Victorian Energy Upgrades (VEU) Accredited Person',
    credentialCategory:   'Accreditation',
    recognizedBy: {
      '@type': 'GovernmentOrganization',
      name:    'Essential Services Commission (ESC) of Victoria',
      url:     'https://www.esc.vic.gov.au/victorian-energy-upgrades',
    },
  },
  url:       BASE_URL,
  telephone: '+61 482 024 485',
  email:     'contact@movementgreen.com.au',
  image:     OG_IMAGE,
  priceRange: 'Free Assessment',
  address: {
    '@type':         'PostalAddress',
    streetAddress:   '20 Timberton Way',
    addressLocality: 'Truganina',
    addressRegion:   'VIC',
    postalCode:      '3029',
    addressCountry:  'AU',
  },
  geo: {
    '@type':    'GeoCoordinates',
    latitude:   -37.8379,
    longitude:  144.7271,
  },
  areaServed: [
    { '@type': 'State', name: 'Victoria', addressCountry: 'AU' },
    { '@type': 'City',  name: 'Melbourne' },
  ],
  openingHoursSpecification: [
    {
      '@type':     'OpeningHoursSpecification',
      dayOfWeek:   ['Monday','Tuesday','Wednesday','Thursday','Friday'],
      opens:       '08:00',
      closes:      '18:00',
    },
    {
      '@type':     'OpeningHoursSpecification',
      dayOfWeek:   ['Saturday'],
      opens:       '09:00',
      closes:      '14:00',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Victorian Energy Upgrade Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Heat Pump Hot Water System Installation',
          description: 'VEU-rebated heat pump hot water system installation — replace gas or electric with up to 70% bill savings.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Reverse-Cycle Air Conditioner Installation',
          description: 'Government-rebated reverse-cycle air conditioner installation in Victoria — up to $7,000 rebate under VEU.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'LED Lighting Upgrade',
          description: 'VEU-eligible LED lighting replacement for Victorian homes and businesses — 80% energy savings.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Commercial Solar Installation',
          description: 'VEU-eligible commercial solar PV installation for Victorian businesses — government rebate available under the VEU program.',
        },
      },
    ],
  },
};
